import { useEffect, useMemo, useState, useCallback } from "react";

export default function LightboxGallery({ images = [], limit = 12 }) {
  // Accept either [{src, alt}] or ["url.jpg"]
  const normalized = useMemo(
    () =>
      images
        .filter(Boolean)
        .slice(0, limit)
        .map((it) =>
          typeof it === "string" ? { src: it, alt: "" } : { alt: "", ...it }
        ),
    [images, limit]
  );

  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const close = useCallback(() => setOpen(false), []);
  const openAt = useCallback((i) => {
    setIndex(i);
    setOpen(true);
  }, []);

  const next = useCallback(
    () => setIndex((i) => (i + 1) % normalized.length),
    [normalized.length]
  );
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + normalized.length) % normalized.length),
    [normalized.length]
  );

  // Lock scroll + keyboard controls
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.body.classList.add("overflow-hidden");
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("overflow-hidden");
      window.removeEventListener("keydown", onKey);
    };
  }, [open, close, next, prev]);

  return (
    <>
      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {normalized.map((img, i) => (
          <button
            key={img.src + i}
            onClick={() => openAt(i)}
            className="relative aspect-square overflow-hidden rounded-xl ring-1 ring-white/10 group"
            aria-label="Open photo"
          >
            <img
              src={img.src}
              alt={img.alt || "Gallery image"}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
              decoding="async"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition" />
          </button>
        ))}
      </div>

      {/* Lightbox */}
      {open && normalized[index] && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm"
          onClick={close}
          aria-modal="true"
          role="dialog"
        >
          <div
            className="absolute inset-0 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={normalized[index].src}
              alt={normalized[index].alt || "Gallery image enlarged"}
              className="max-h-[88vh] max-w-[90vw] rounded-xl shadow-2xl"
            />
            {/* Prev / Next */}
            {normalized.length > 1 && (
              <>
                <button
                  onClick={prev}
                  className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 px-4 py-3"
                  aria-label="Previous image"
                >
                  ‹
                </button>
                <button
                  onClick={next}
                  className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 px-4 py-3"
                  aria-label="Next image"
                >
                  ›
                </button>
              </>
            )}
            {/* Close */}
            <button
              onClick={close}
              className="absolute top-4 right-4 md:top-6 md:right-6 rounded-full bg-white text-black px-3 py-1.5 text-sm font-semibold hover:opacity-90"
              aria-label="Close"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}